// Durable, org-scoped record of a human-approved robot onboarding plan.
// The robot-onboarding agent proposes a plan, the human approves it, and
// Cortex stores that approval here. Status then tracks apply: approved →
// applied → live | failed, or superseded when a later plan replaces it.
// Dates on the wire are ISO-8601 strings.
import { z } from 'zod';
export const RobotApprovedPlanStatusSchema = z.enum([
    'approved',
    'applied',
    'live',
    'failed',
    'superseded',
]);
/**
 * Body of POST /v1/robot-plans. Upsert key is (orgId, planId); orgId and
 * approvedByUserId come from the authenticated caller, not this body.
 * `status` defaults to `approved` — the moment the human approves, before apply.
 */
export const SaveRobotApprovedPlanSchema = z.object({
    robotSourceId: z.string(),
    robotName: z.string().optional(),
    deviceId: z.string().optional(),
    deviceName: z.string().optional(),
    planId: z.string(),
    planHash: z.string(),
    threadId: z.string().optional(),
    approvalRequestId: z.string().optional(),
    question: z.string(),
    plan: z.record(z.unknown()),
    status: RobotApprovedPlanStatusSchema.default('approved'),
});
/** Body of PATCH /v1/robot-plans/:id/status. Receipt and device are filled in as apply proceeds. */
export const UpdateRobotApprovedPlanStatusSchema = z.object({
    status: RobotApprovedPlanStatusSchema,
    deviceId: z.string().optional(),
    deviceName: z.string().optional(),
    receipt: z.record(z.unknown()).optional(),
});
/** One stored approval. Nullable columns are null on the wire, not omitted. */
export const RobotApprovedPlanSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    robotSourceId: z.string(),
    robotName: z.string().nullable(),
    deviceId: z.string().nullable(),
    deviceName: z.string().nullable(),
    planId: z.string(),
    planHash: z.string(),
    threadId: z.string().nullable(),
    approvalRequestId: z.string().nullable(),
    approvedByUserId: z.string().nullable(),
    question: z.string(),
    plan: z.record(z.unknown()),
    status: RobotApprovedPlanStatusSchema,
    receipt: z.record(z.unknown()).nullable(),
    approvedAt: z.string().datetime(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * Query of GET /v1/robot-plans. `limit` is coerced so query-string values
 * (`?limit=20`) and JSON numbers (MCP tools) both parse. Default 20, max 100.
 */
export const ListRobotApprovedPlansQuerySchema = z.object({
    robotSourceId: z.string().optional(),
    deviceId: z.string().optional(),
    status: RobotApprovedPlanStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
//# sourceMappingURL=robot-approved-plan.js.map